// KaziScore card (M2, employee-only shadow) — the score IS its explanation:
// ring + band, per-factor bars with plain-language detail, deterministic
// improvement tips, and the change feed. Fails silent: no score, no card.
import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { creditApi, type KaziScore, type ScoreFeedEntry } from '@/features/credit/api/creditApi';

const BAND_STYLE: Record<string, { label: string; cls: string }> = {
  building: { label: 'Building', cls: 'bg-muted text-muted-foreground' },
  bronze: { label: 'Bronze', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400' },
  silver: { label: 'Silver', cls: 'bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300' },
  gold: { label: 'Gold', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-400' },
  platinum: { label: 'Platinum', cls: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-300' },
  diamond: { label: 'Diamond', cls: 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300' },
};

const FACTOR_LABEL: Record<string, string> = {
  tenure: 'Time with employer',
  identity: 'Identity',
  repayment: 'Repayment',
  debt_load: 'Headroom',
  completed: 'Completed products',
};

export function KaziScoreCard() {
  const [score, setScore] = useState<KaziScore | null>(null);
  const [feed, setFeed] = useState<ScoreFeedEntry[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    creditApi.myScore().then(setScore).catch(() => setScore(null));
  }, []);
  useEffect(() => {
    if (open && feed.length === 0) {
      creditApi.myScoreHistory().then(setFeed).catch(() => {});
    }
  }, [open, feed.length]);

  if (!score) return null;
  const band = BAND_STYLE[score.band] ?? BAND_STYLE.building;
  const pct = Math.min(100, Math.max(0, score.score / 10));
  const R = 34;
  const C = 2 * Math.PI * R;

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center gap-5">
          <div className="relative h-20 w-20 shrink-0">
            <svg viewBox="0 0 80 80" className="h-20 w-20 -rotate-90">
              <circle cx="40" cy="40" r={R} fill="none" strokeWidth="7" className="stroke-muted" />
              <circle
                cx="40" cy="40" r={R} fill="none" strokeWidth="7" strokeLinecap="round"
                className="stroke-orange-500"
                strokeDasharray={`${(pct / 100) * C} ${C}`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-foreground">{score.score}</span>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-foreground">KaziScore</h2>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${band.cls}`}>{band.label}</span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Your trust profile, built from your employment and repayment history. Only you can see it.
            </p>
            {score.suggestions[0] && (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-foreground">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-500" />
                {score.suggestions[0]}
              </p>
            )}
          </div>

          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            Why? {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {open && (
          <div className="mt-5 grid gap-6 border-t border-border pt-5 md:grid-cols-2">
            <div className="space-y-3">
              {score.factors.map((f) => (
                <div key={f.key}>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="font-medium text-foreground">{FACTOR_LABEL[f.key] ?? f.key}</span>
                    <span className="text-muted-foreground">{f.points}/{f.max}</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full ${f.rating === 'excellent' ? 'bg-emerald-500' : f.rating === 'good' ? 'bg-orange-400' : 'bg-slate-400'}`}
                      style={{ width: `${Math.min(100, (f.points / f.max) * 100)}%` }}
                    />
                  </div>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{f.detail}</p>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent changes</h3>
              {feed.length === 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">No changes yet — your score updates as your history grows.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {feed.slice(0, 6).map((e, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      <span className={`mt-0.5 min-w-[2.5rem] text-right font-semibold ${e.delta > 0 ? 'text-success' : e.delta < 0 ? 'text-danger' : 'text-muted-foreground'}`}>
                        {e.delta > 0 ? `+${e.delta}` : e.delta === 0 ? '•' : e.delta}
                      </span>
                      <span className="text-muted-foreground">
                        {e.detail} · {formatDate(e.occurred_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
